import { Document, StyleSheet, Text, View } from '@react-pdf/renderer';
import { VoteResultByShareholderType } from '../types';
import ReportCoverPage from '@/components/Shared/Reports/ReportCoverPage';
import ReportSectionHeader from '@/components/Shared/Reports/components/ReportSectionHeader';
import ReportsTable, { ReportsTableConfig } from '@/components/Shared/Reports/components/ReportsTable';
import ReportTemplatePage, { ReportHeaderInfo } from '@/components/Shared/Reports/ReportTemplatePage';
import { TFunction } from 'i18next';

type VotingResultsByShareholderReportPdfProps = {
  companyLogo?: string;
  meetingName: string;
  voteResults: VoteResultByShareholderType[];
  reportHeaderInfo: ReportHeaderInfo;
  t: TFunction<'vote', 'vote'>;
  currentLanguage: string;
};

const rowStyles = StyleSheet.create({
  tableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopRightRadius: 6,
    borderTopLeftRadius: 6,
    fontSize: 8,
    fontWeight: 'bold',
    color: '#271060',
    textAlign: 'center',
    backgroundColor: '#EDEFF4',
  },

  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
    paddingHorizontal: 8,
    fontSize: 8,
    textAlign: 'center',
    color: '#271060',
  },

  tableRowWithBg: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
    paddingHorizontal: 8,
    fontSize: 8,
    color: '#271060',
    textAlign: 'center',
    backgroundColor: '#F6F7FA',
  },

  shareholderHeader: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: 'Janna',
    fontSize: 12,
    fontWeight: 'bold',
    color: '#2A186A',
  },

  shareholderName: {
    width: '33.33%',
  },

  shareholderMetric: {
    width: '33.34%',
    textAlign: 'center',
  },

  shareholderOwnership: {
    width: '33.33%',
  },
});

const VotingResultsByShareholderReportPdf = ({
  meetingName,
  companyLogo,
  voteResults,
  reportHeaderInfo,
  t,
  currentLanguage,
}: VotingResultsByShareholderReportPdfProps) => {
  const getAllClausesVotes = (shareholderItem: VoteResultByShareholderType) => {
    return Object.keys(shareholderItem).reduce(
      (acc, curr) => {
        if (!['name', 'id', 'total_shares', 'ownership'].includes(curr)) {
          acc.push({
            clause: curr,
            vote: t(shareholderItem[curr].value ?? 'no vote'),
            point_description: shareholderItem[curr].point_description || '',
            total_shares: shareholderItem.total_shares,
            ownership: shareholderItem.ownership,
          });
        }
        return acc;
      },
      [] as {
        clause: string;
        vote: string;
        point_description: string;
        total_shares: number;
        ownership: number;
      }[],
    );
  };

  const columns: ReportsTableConfig[] = [
    {
      key: 'clause',
      title: t('clause'),
      dataSourceKey: 'clause',
      width: '5%',
      textAlign: 'center',
    },
    {
      key: 'point_description',
      title: t('pointDescription'),
      dataSourceKey: 'point_description',
      width: '80%',
      textAlign: currentLanguage === 'ar' ? 'right' : 'left',
    },
    {
      key: 'vote',
      title: t('vote'),
      dataSourceKey: 'vote',
      width: '15%',
      textAlign: 'center',
    },
  ];

  return (
    <Document>
      <ReportCoverPage reportNameAddtion={t('shareholders')} reportName={meetingName} companyLogo={companyLogo} />
      {(voteResults ?? []).map((item) => (
        <ReportTemplatePage
          key={item.id || item.name}
          reportName={t('shareholders')}
          reportHeaderInfo={reportHeaderInfo}
          hasCurrency={false}
        >
          <ReportSectionHeader
            title={
              <View
                style={
                  currentLanguage === 'ar'
                    ? [rowStyles.shareholderHeader, { flexDirection: 'row-reverse' }]
                    : rowStyles.shareholderHeader
                }
              >
                <Text style={[rowStyles.shareholderName, { textAlign: currentLanguage === 'ar' ? 'right' : 'left' }]}>
                  {item.name}
                </Text>
                <Text style={rowStyles.shareholderMetric}>
                  {t('totalShares') + ` ${item.total_shares.toLocaleString()}`}
                </Text>
                <Text
                  style={[rowStyles.shareholderOwnership, { textAlign: currentLanguage === 'ar' ? 'left' : 'right' }]}
                >
                  {t('ownership') + ` %${item.ownership.toFixed(4)}`}
                </Text>
              </View>
            }
            currentLanguage={currentLanguage}
          />
          <View style={{ marginBottom: 8 }}></View>
          <ReportsTable
            tableConfig={currentLanguage === 'ar' ? [...columns].reverse() : columns}
            dataSource={getAllClausesVotes(item).map((row) => ({
              clause: row.clause,
              point_description: row.point_description,
              vote: row.vote,
            }))}
            rowStyle={rowStyles.tableRow}
            rowStyleWithBg={rowStyles.tableRowWithBg}
            tableHeaderStyle={rowStyles.tableHeader}
          />
        </ReportTemplatePage>
      ))}
    </Document>
  );
};

export default VotingResultsByShareholderReportPdf;
